import clsx from "clsx";
import Image from "next/image";
import { useTranslations } from "next-intl";

import { WAVEFORM_BAR_WIDTH, WAVEFORM_HEIGHTS_1, WAVEFORM_HEIGHTS_2 } from "./helpers";

// Image assets
import imgTimeline from "./assets/studio-editor-demo/timeline.png";
import imgVoiceBg05 from "@/app/_blocks/BlockVoicesSlider/images/Voice BG 05.png";

// Icon assets - main
import IconBigT from "./assets/studio-editor-demo/big-t.svg";
import IconRemoveBackground from "./assets/studio-editor-demo/remove-background.svg";
import IconVoiceChanger from "./assets/studio-editor-demo/voice-changer.svg";
import IconRecordAudio from "./assets/studio-editor-demo/record-audio.svg";

// Icon assets - sidebar
import IconArrow from "./assets/studio-editor-demo/sidebar-icons/arrow.svg";
import IconBang from "./assets/studio-editor-demo/sidebar-icons/bang.svg";
import IconCamera from "./assets/studio-editor-demo/sidebar-icons/camera.svg";
import IconCaption from "./assets/studio-editor-demo/sidebar-icons/caption.svg";
import IconFolderUpload from "./assets/studio-editor-demo/sidebar-icons/folder-upload.svg";
import IconMusic from "./assets/studio-editor-demo/sidebar-icons/music.svg";
import IconText from "./assets/studio-editor-demo/sidebar-icons/text.svg";

// Constants - text content (intentionally not localized - sample content)
const TEXT_PARAGRAPH_1 =
  "In the stillness of the court, a new standard is set. Precision meets legacy, and every movement carries the confidence of those who know they belong here. This is where discipline becomes artistry, and tradition steps into the present.";
const TEXT_PARAGRAPH_2 =
  "Out here, the game is elevated. Grass underfoot, silence between points, excellence without compromise. Every rally is deliberate. Every detail intentional. Belmont is not about playing more — it's about playing better.";
const TEXT_PARAGRAPH_3 =
  "Beyond the match, Belmont Lawn Club stands as a destination. Built for those who demand more from the game, and from themselves. This is where tennis becomes timeless.";

// Constants - timeline
const TIMELINE_SECONDS = 20;
const LABELED_SECONDS: Record<number, string> = {
  2: "0:02",
  5: "0:05",
  10: "0:10",
};

// Constants - icons
const SIDEBAR_ICONS = [IconArrow, IconBang, IconMusic, IconCamera, IconFolderUpload, IconText, IconCaption];

// Types
type FormFieldProps = {
  label: string;
  value: string;
  icon?: React.ReactNode;
  className?: string;
};

type AITool = {
  labelKey: "remove_background" | "voice_changer" | "record_audio";
  icon?: React.ReactNode;
};

// Constants - AI tools
const AI_TOOLS: AITool[] = [
  { labelKey: "remove_background", icon: <IconRemoveBackground className='tw-size-3 tw-shrink-0 tw-text-black' /> },
  { labelKey: "voice_changer", icon: <IconVoiceChanger className='tw-size-3 tw-shrink-0 tw-text-black' /> },
  { labelKey: "record_audio", icon: <IconRecordAudio className='tw-size-3 tw-shrink-0 tw-text-black' /> },
];

// Components
function FormField({ label, value, icon, className }: FormFieldProps) {
  return (
    <div className={clsx("tw-w-full tw-min-w-0", className)}>
      <div className='tw-text-black tw-mb-1.5 tw-leading' style={{ fontSize: "0.5625rem", lineHeight: 1.4 }}>
        {label}
      </div>
      <div className='tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-border tw-border-black/10 tw-rounded-lg tw-min-w-0'>
        {icon}
        <span className='tw-text-black tw-truncate' style={{ fontSize: "0.625rem", lineHeight: 1.4 }}>
          {value}
        </span>
      </div>
    </div>
  );
}

function AIToolsField({ className }: { className?: string }) {
  const t = useTranslations("ClientKeys.MockDashboards.StudioEditor");

  return (
    <div className={clsx("tw-w-full", className)}>
      <div className='tw-text-black tw-mb-1.5' style={{ fontSize: "0.5625rem", lineHeight: 1.4 }}>
        {t("ai_tools")}
      </div>
      <div className='tw-flex tw-flex-col tw-divide-y tw-divide-black/10 tw-border tw-border-black/10 tw-rounded-lg tw-overflow-hidden'>
        {AI_TOOLS.map(tool => (
          <div key={tool.labelKey} className='tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-min-w-0'>
            {tool.icon}
            <span className='tw-text-black tw-truncate' style={{ fontSize: "0.625rem", lineHeight: 1.4 }}>
              {t(tool.labelKey)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function IconStrip({ className }: { className?: string }) {
  return (
    <div
      className={clsx(
        "tw-relative tw-h-full tw-w-10 tw-overflow-hidden tw-border-r tw-border-black/5 tw-flex-shrink-0",
        className,
      )}
    >
      <div className='tw-flex tw-flex-col tw-items-center tw-gap-4 tw-py-6'>
        {SIDEBAR_ICONS.map((Icon, i) =>
          i === 0 ? (
            <div
              key={i}
              className='tw-size-6 tw-rounded-md tw-bg-black/5 tw-flex tw-items-center tw-justify-center tw-shrink-0'
            >
              <Icon className='tw-size-3.5 tw-shrink-0' />
            </div>
          ) : (
            <Icon key={i} className='tw-size-3.5 tw-shrink-0' />
          ),
        )}
      </div>
      <div className='tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-12 tw-bg-gradient-to-t tw-from-white tw-to-transparent tw-pointer-events-none' />
    </div>
  );
}

function PropertiesPanel({ className }: { className?: string }) {
  const t = useTranslations("ClientKeys.MockDashboards.StudioEditor");

  return (
    <div
      className={clsx(
        "tw-flex tw-flex-col tw-gap-3.5 tw-px-4 tw-border-r tw-border-black/5 tw-py-6 tw-flex-shrink-0",
        className,
      )}
    >
      <FormField
        label={t("type")}
        value={t("text")}
        icon={<IconBigT className='tw-size-3 tw-shrink-0 tw-text-black' />}
      />
      <FormField
        label={t("voice")}
        value='Rachel'
        icon={
          <Image src={imgVoiceBg05} alt='' className='tw-size-3 tw-rounded-full tw-flex-shrink-0 tw-object-cover' />
        }
      />
      <AIToolsField />
    </div>
  );
}

function ContentArea({ className }: { className?: string }) {
  return (
    <div className={clsx("tw-relative tw-h-full tw-overflow-hidden", className)}>
      <div className='tw-flex tw-flex-col tw-gap-4 md:tw-gap-6 lg:tw-gap-8'>
        <p className='tw-text-[0.5625rem] md:tw-text-[0.625rem] lg:tw-text-[0.6875rem] tw-leading-[1.5] tw-text-black'>
          {TEXT_PARAGRAPH_1}
        </p>
        <p className='tw-text-[0.5625rem] md:tw-text-[0.625rem] lg:tw-text-[0.6875rem] tw-leading-[1.5] tw-text-black tw-opacity-60'>
          {TEXT_PARAGRAPH_2}
        </p>
        <p className='tw-text-[0.5625rem] md:tw-text-[0.625rem] lg:tw-text-[0.6875rem] tw-leading-[1.5] tw-text-black tw-opacity-30'>
          {TEXT_PARAGRAPH_3}
        </p>
      </div>
      {/* Gradient fade to indicate truncation */}
      <div className='tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-12 tw-bg-gradient-to-t tw-from-white tw-to-transparent tw-pointer-events-none' />
    </div>
  );
}

function TimelineMarkers() {
  return (
    <div className='tw-flex tw-items-center tw-h-4 tw-gap-9 tw-mb-1'>
      {Array.from({ length: TIMELINE_SECONDS }, (_, i) => i + 1).map(second => {
        const label = LABELED_SECONDS[second];
        return label ? (
          <span
            key={second}
            className='tw-text-black/60 tw-leading-none tw-flex tw-justify-center tw-w-0'
            style={{ fontSize: "0.5625rem" }}
          >
            {label}
          </span>
        ) : (
          <div
            key={second}
            className='tw-rounded-full tw-bg-black/30 tw-flex-shrink-0'
            style={{ width: "0.156rem", height: "0.156rem" }}
          />
        );
      })}
    </div>
  );
}

function VideoStrip() {
  return (
    <div className='tw-w-full tw-h-7 tw-rounded-lg tw-relative tw-overflow-hidden'>
      <Image src={imgTimeline} alt='' fill className='tw-object-cover' placeholder='blur' sizes='100%' quality={90} />
    </div>
  );
}

function WaveformBars({ heights }: { heights: number[] }) {
  return (
    <div className='tw-flex tw-items-end tw-h-full tw-opacity-10'>
      {heights.map((height, i) => (
        <div
          key={i}
          className='tw-bg-black tw-flex-shrink-0'
          style={{ height: `${height}rem`, width: `${WAVEFORM_BAR_WIDTH}rem` }}
        />
      ))}
    </div>
  );
}

function VoiceOverTrack({ label, className, heights }: { label: string; className?: string; heights: number[] }) {
  return (
    <div
      className={clsx(
        "tw-h-7 tw-rounded-lg tw-overflow-hidden tw-relative tw-bg-opacity-15 tw-border-opacity-15",
        className,
      )}
      style={{
        borderWidth: "0.03125rem",
        backgroundColor: "rgba(159, 87, 0, 0.15)",
        borderColor: "rgba(159, 87, 0, 0.15)",
      }}
    >
      <span
        className='tw-absolute tw-left-1.5 tw-top-1 tw-text-black tw-opacity-50 tw-leading-none'
        style={{ fontSize: "0.5rem" }}
      >
        {label}
      </span>
      <div className='tw-absolute tw-inset-0 tw-flex tw-items-center'>
        <WaveformBars heights={heights} />
      </div>
    </div>
  );
}

function VoiceOverTracks() {
  const t = useTranslations("ClientKeys.MockDashboards.StudioEditor");

  return (
    <div className='tw-flex tw-gap-1 tw-w-full'>
      <VoiceOverTrack label={t("voice_over_1")} className='tw-flex-[3]' heights={WAVEFORM_HEIGHTS_1} />
      <VoiceOverTrack label={t("voice_over_2")} className='tw-flex-[2]' heights={WAVEFORM_HEIGHTS_2} />
    </div>
  );
}

function TimelineSection({ className }: { className?: string }) {
  return (
    <div
      className={clsx(
        "tw-flex tw-flex-col tw-gap-2 tw-pb-4 tw-flex-shrink-0 tw-border-t tw-border-black/10",
        className,
      )}
    >
      <div className='tw-px-4 lg:tw-px-6 xl:tw-px-10 tw-flex tw-flex-col tw-gap-1'>
        <TimelineMarkers />
        <VideoStrip />
        <VoiceOverTracks />
      </div>
    </div>
  );
}

/**
 * StudioEditorMockDashboard - Pure UI component for the Studio Editor dashboard
 * This is a presentational component without any animation or BlockStickyContentList logic.
 */
export function StudioEditorMockDashboard() {
  return (
    <div className='tw-h-full tw-bg-white tw-rounded-bl-xl tw-overflow-hidden tw-flex tw-flex-col'>
      <div className='tw-flex tw-flex-1 tw-min-h-0 tw-overflow-hidden'>
        <IconStrip />
        <div className='tw-flex tw-flex-1 tw-min-w-0'>
          <PropertiesPanel className='tw-w-2/5' />
          <div className='tw-w-3/5 tw-flex tw-overflow-hidden'>
            <ContentArea className='tw-py-10 tw-px-8' />
          </div>
        </div>
      </div>
      <TimelineSection />
    </div>
  );
}
